<template>
  <div class="chat-tabbar" role="tablist" aria-label="Tab percakapan">
    <div class="chat-tabbar-scroll">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        role="tab"
        class="chat-tab"
        :class="{ 'is-active': tab.active, 'is-streaming': tab.streaming, 'has-error': tab.hasError }"
        :aria-selected="tab.active"
        :title="tab.label"
        draggable="true"
        @click="$emit('select', tab.id)"
        @dragstart="onDragStart(tab.id, $event)"
        @dragover.prevent
        @drop="onDrop(tab.id)"
        data-component-id="chat--tabbar--tab"
      >
        <span class="chat-tab-state" aria-hidden="true">
          <span v-if="tab.streaming" class="chat-tab-spinner"></span>
          <span v-else-if="tab.hasError" class="chat-tab-dot chat-tab-dot-err">!</span>
          <span v-else class="chat-tab-dot">{{ tab.questionCount || '·' }}</span>
        </span>
        <span class="chat-tab-label">{{ tab.label }}</span>
        <span
          v-if="tabs.length > 1"
          class="chat-tab-close"
          role="button"
          :title="`Tutup tab — ${tab.label}`"
          aria-label="Tutup tab"
          @click.stop="$emit('close', tab.id)"
        >✕</span>
      </button>
    </div>

    <button
      type="button"
      class="chat-tab-new"
      title="Tab percakapan baru"
      aria-label="Tab baru"
      @click="$emit('new')"
      data-component-id="chat--tabbar--btn-new"
    >＋</button>
  </div>
</template>

<script setup>
import { ref } from 'vue'

defineProps({
  tabs: { type: Array, default: () => [] },
})
const emit = defineEmits(['select', 'close', 'new', 'reorder'])

const dragId = ref(null)
function onDragStart(id, e) {
  dragId.value = id
  try { e.dataTransfer.effectAllowed = 'move' } catch { /* ignore */ }
}
function onDrop(targetId) {
  if (dragId.value && dragId.value !== targetId) {
    emit('reorder', { fromId: dragId.value, toId: targetId })
  }
  dragId.value = null
}
</script>

<style scoped>
.chat-tabbar {
  display: flex; align-items: stretch; gap: 4px;
  flex-shrink: 0; padding: 4px 6px;
  background: var(--bg-elev, var(--bg-card));
  border-bottom: 1px solid var(--border-soft, var(--border-light));
}
.chat-tabbar-scroll {
  display: flex; align-items: stretch; gap: 4px;
  flex: 1; min-width: 0; overflow-x: auto;
  scrollbar-width: thin;
}
.chat-tabbar-scroll::-webkit-scrollbar { height: 4px; }
.chat-tabbar-scroll::-webkit-scrollbar-thumb {
  background: var(--border-soft, var(--border-light)); border-radius: 2px;
}

.chat-tab {
  display: inline-flex; align-items: center; gap: 6px;
  max-width: 190px; min-width: 96px;
  padding: 5px 8px; border-radius: 7px 7px 0 0;
  background: transparent; cursor: pointer;
  border: 1px solid transparent; border-bottom: none;
  color: var(--text-muted, var(--text-ghost, #888));
  font-family: inherit; font-size: 11.5px; line-height: 1.2;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.chat-tab:hover { background: var(--bg-hover, rgba(255,255,255,0.04)); color: var(--text-base, var(--text-body)); }
.chat-tab.is-active {
  background: var(--bg-base, var(--bg-deep));
  border-color: var(--border-soft, var(--border-light));
  color: var(--text-primary, var(--text-body));
  font-weight: 600;
}
.chat-tab.is-active .chat-tab-dot {
  background: var(--accent, var(--accent-blue, #3b82f6)); color: #fff;
}
.chat-tab.has-error:not(.is-active) { color: var(--accent-red-light, #f87171); }

.chat-tab-state { display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
.chat-tab-dot {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 15px; height: 15px; padding: 0 3px; border-radius: 8px;
  background: var(--bg-elev, var(--bg-card));
  border: 1px solid var(--border-soft, var(--border-light));
  font-size: 9px; font-weight: 700; font-variant-numeric: tabular-nums;
  line-height: 1;
}
.chat-tab-dot-err { background: var(--accent-red, #ef4444); color: #fff; border-color: transparent; }

.chat-tab-spinner {
  width: 12px; height: 12px; border-radius: 50%;
  border: 2px solid color-mix(in srgb, var(--accent, #3b82f6) 30%, transparent);
  border-top-color: var(--accent, var(--accent-blue, #3b82f6));
  animation: chat-tab-spin 0.7s linear infinite;
}
@keyframes chat-tab-spin { to { transform: rotate(360deg); } }

.chat-tab-label {
  flex: 1; min-width: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.chat-tab-close {
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px; border-radius: 50%;
  font-size: 10px; opacity: 0.55;
  transition: background 0.12s, opacity 0.12s;
}
.chat-tab-close:hover { opacity: 1; background: var(--accent-red, #ef4444); color: #fff; }

.chat-tab-new {
  flex-shrink: 0; align-self: center;
  width: 26px; height: 26px; border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center;
  background: transparent; cursor: pointer;
  border: 1px solid var(--border-soft, var(--border-light));
  color: var(--text-base, var(--text-body));
  font-size: 15px; line-height: 1;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.chat-tab-new:hover {
  border-color: var(--accent, var(--accent-blue, #3b82f6));
  color: var(--accent, var(--accent-blue, #3b82f6));
  background: var(--bg-hover, rgba(255,255,255,0.04));
}

@media (max-width: 480px) {
  .chat-tab { min-width: 76px; max-width: 140px; }
}
</style>
